import React from "react";
import { CHART_1, CHART_2, CHART_3, INK, MUTED, GRID, PAPER } from "./palette";
export { CHART_1, CHART_2, CHART_3, GRID, MUTED, INK, PAPER } from "./palette";

// The pieces of ConceptChart.jsx the cluster charts (ClustersChart, ClusterProfilesChart,
// DendrogramChart) use, copied so those charts live in their own files. Keep in step with
// ConceptChart.jsx: same tokens, same 640-wide viewBox, same legend, tooltip and axes.
export const CLUSTER_COLORS = [CHART_1, CHART_2, CHART_3];
// Past three clusters, fall back to gray rather than invent a fourth hue.
export const clusterColor = (i) => (i == null ? MUTED : CLUSTER_COLORS[i] || MUTED);

export const W = 640;
export const M = { top: 16, right: 16, bottom: 36, left: 16 };

export const fmt = (x, places = 2) => (x < 0 ? "−" : "") + Math.abs(x).toFixed(places);
export const signed = (v, places = 2) => (v > 0 ? `+${fmt(v, places)}` : fmt(v, places));
export const count = (n) => n.toLocaleString("en-US");
export const plural = (n, noun) => `${count(n)} ${n === 1 ? noun : `${noun}s`}`;
export const lowerFirst = (s) => (/^.[A-Z0-9]/.test(s) ? s : s.charAt(0).toLowerCase() + s.slice(1));
export const sentenceList = (parts) => (parts.length < 2 ? parts.join("") : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`);
const minus = (t) => (t < 0 ? `−${-t}` : t);

// Nice axis bounds and ticks around a set of values.
export function axis(xs, pad = 0.05) {
	const lo = Math.min(...xs), hi = Math.max(...xs), span = hi - lo || 1;
	const rough = span / 6, mag = 10 ** Math.floor(Math.log10(rough));
	const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= rough);
	const min = Math.max(lo >= 0 ? 0 : -Infinity, Math.floor((lo - span * pad) / step) * step), max = Math.ceil((hi + span * pad) / step) * step;
	const ticks = [];
	for (let t = min; t <= max + step / 2; t += step) ticks.push(+t.toFixed(10));
	return { min, max, ticks };
}

export const scale = (ax, a, b) => (v) => a + ((v - ax.min) / (ax.max - ax.min)) * (b - a);

// Spread labels that would overlap: keep each at least `gap` px from the one above.
export function spread(items, gap = 14) {
	const sorted = [...items].sort((a, b) => a.y - b.y);
	sorted.forEach((it, i) => { if (i > 0 && it.y - sorted[i - 1].y < gap) it.y = sorted[i - 1].y + gap; });
	return items;
}

export const Halo = ({ children, ...props }) => <text fontSize="11" stroke={PAPER} strokeWidth="3" paintOrder="stroke" strokeLinejoin="round" {...props}>{children}</text>;

// kind: dot (default), line, dash, square, ring.
export function Legend({ items, title }) {
	const swatch = (kind, color) => {
		switch (kind) {
			case "line": return <span className="inline-block h-0.5 w-4 rounded" style={{ background: color }} />;
			case "dash": return <span className="inline-block w-4 border-t-2 border-dashed" style={{ borderColor: color }} />;
			case "square": return <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} />;
			case "ring": return <span className="inline-block h-2.5 w-2.5 rounded-full border-[1.5px]" style={{ borderColor: INK }} />;
			default: return <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: color }} />;
		}
	};
	return (
		<div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600">
			{title && <span className="font-semibold text-gray-700">{title}</span>}
			{items.map(({ label, color, kind }) => (
				<span key={label} className="flex items-center gap-1.5">
					{swatch(kind, color)}
					{label}
				</span>
			))}
		</div>
	);
}

export function Tooltip({ x, lines }) {
	const longest = Math.max(...lines.map((l) => String(l).length));
	const width = Math.max(170, Math.round(longest * 6.4) + 16), left = Math.min(Math.max(x + 8, M.left), W - M.right - width);
	return (
		<g pointerEvents="none" transform={`translate(${left},${M.top})`}>
			<rect width={width} height={10 + lines.length * 15} rx="3" fill={PAPER} stroke={GRID} />
			{lines.map((line, i) => (
				<text key={i} x="8" y={17 + i * 15} fontSize="11" fill={i === 0 ? MUTED : INK} fontWeight={i === 0 ? 400 : 600}>{line}</text>
			))}
		</g>
	);
}

// Gridlines and tick labels for a box { l, r, t, b }, with the axis titles.
export function Axes({ axX, axY, x, y, box, xLabel, yLabel, yFormat = minus }) {
	return (
		<g>
			{axY.ticks.map((t) => (
				<g key={`y${t}`}>
					<line x1={box.l} x2={box.r} y1={y(t)} y2={y(t)} stroke={GRID} />
					<text x={box.l - 6} y={y(t)} fontSize="11" fill={MUTED} textAnchor="end" dominantBaseline="middle">{yFormat(t)}</text>
				</g>
			))}
			{axX.ticks.map((t) => <text key={`x${t}`} x={x(t)} y={box.b + 16} fontSize="11" fill={MUTED} textAnchor="middle">{minus(t)}</text>)}
			{xLabel && <text x={box.r} y={box.b + 32} fontSize="11" fill={MUTED} textAnchor="end">{xLabel}</text>}
			{yLabel && <text x={box.l} y={box.t - 8} fontSize="11" fill={MUTED}>{yLabel}</text>}
		</g>
	);
}
